import { useToast } from '@chakra-ui/react';
import { PersonalizedBannerItem } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import React, { Suspense, useCallback, useMemo, useState } from 'react';

const LazyFormBanner = React.lazy(() =>
  import('./form-banner').then((m) => ({ default: m.FormBanner }))
);

type Mode = 'create' | 'edit' | 'duplicate';

interface BannerModalManagerProps {
  data: PersonalizedBannerItem[];
  /** Parsed at BannerPage from the full legacy banner list. */
  nextIndex: number;
  onSuccess: () => void;
  isNewCg3?: boolean;
  children: (handlers: {
    onAdd: () => void;
    onEdit: (id: string) => void;
    onDuplicate: (id: string) => void;
  }) => React.ReactNode;
}

export function BannerModalManager({
  data,
  nextIndex,
  onSuccess,
  children,
  isNewCg3 = false,
}: BannerModalManagerProps) {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('create');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const onClose = useCallback(() => {
    setIsOpen(false);
    setSelectedId(null);
    setMode('create');
  }, []);

  const onAdd = useCallback(() => {
    setSelectedId(null);
    setMode('create');
    setIsOpen(true);
  }, []);

  const onEdit = useCallback((id: string) => {
    setSelectedId(id);
    setMode('edit');
    setIsOpen(true);
  }, []);

  const onDuplicate = useCallback(
    (id: string) => {
      if (!isNewCg3) {
        showToast(toast, 'info', 'Duplikasi tidak tersedia pada mode legacy');
        return;
      }
      setSelectedId(id);
      setMode('duplicate');
      setIsOpen(true);
    },
    [isNewCg3, toast]
  );

  const editingBanner = useMemo(() => {
    if (mode !== 'edit' || !selectedId) return null;
    return data.find((b) => b.id === selectedId) ?? null;
  }, [data, mode, selectedId]);

  const duplicateFrom = useMemo(() => {
    if (mode !== 'duplicate' || !selectedId) return null;
    return data.find((b) => b.id === selectedId) ?? null;
  }, [data, mode, selectedId]);

  const formKey = `${mode}:${selectedId ?? 'create'}`;

  return (
    <>
      {children({ onAdd, onEdit, onDuplicate })}
      <Suspense fallback={null}>
        <LazyFormBanner
          key={formKey}
          nextIndex={nextIndex}
          isOpen={isOpen}
          onClose={onClose}
          onSuccess={() => {
            onSuccess();
          }}
          editingBanner={editingBanner}
          duplicateFrom={duplicateFrom}
          isNewCg3={isNewCg3}
        />
      </Suspense>
    </>
  );
}
